/**
 * Morse — die Anzeige der Hörspur: der Knopf, die Karte und der Dienst für die Seite. (07.10.2026)
 *
 * Martin: „… dass man sich anhören kann, wie alles funktioniert. Also eine Audio-Spur zum Anhören.“
 *
 * Ein PLATZ ist ein Stück Seite mit zwei Gesichtern, die sich abwechseln:
 *   - ruhig: der Knopf „▶ Vorführung anhören“ (oder „Alle der Reihe nach“) mit einer Zeile darunter (beim kleinen Platz
 *     der Tabelle statt der Zeile ein Tooltip) — und, wenn eine
 *     Übung der Hörspur weichen musste, „Zurück zur Übung“;
 *   - spielt: die Karte — Zustand in Wort UND Form, das Kapitel (die EINE Live-Region: sie sagt nur, wo man ist),
 *     die mitlaufende Textzeile (Untertitel, keine Live-Region: jede Zeile vorzulesen wäre Lärm über dem Ton), die
 *     Bilder der Zeichen (leuchten mit dem Ton), ein nativer Fortschrittsbalken (`<progress>`, ohne Dauer-
 *     Animation), „Pause/Weiter“, „Stopp“ und — bei der Vorführung — „Voriges/Nächstes“ Kapitel.
 * Es gibt nur EINEN Abspieler; ist er aktiv, zeigt NUR der Platz, der ihn gestartet hat, seine Karte — die Knöpfe
 * der anderen Plätze verschwinden (kein zweiter Start).
 *
 * Aufnahmen (07.10.2026, Martin: „Der Button geht nicht.“ — der Grund stand nur als blasse Kleinschrift): Ist die
 * Tagesaufnahme für die Spur angehalten, sagt es die Karte in normaler Schrift (unter den Knöpfen — wenn der Satz
 * verschwindet, rückt nichts unter dem Finger). Kann die Spur nicht spielen (Sprechtaste, Tagesaufnahme ließ sich nicht
 * anhalten), steht der Satz als Kasten unter dem Knopf (`role=status`, Art „hinweis“), nicht als Nebentext.
 *
 * Die Leertaste bedient den Pause-Knopf von selbst, wenn er den Fokus hat (ein echter `<button>`); sonst gehört
 * sie der Seite wie bisher.
 *
 * ⚠️ Keine Inline-Styles: Zustände stehen als data-Attribute, das Aussehen in style.css (`.morse-spur-…`).
 */
import { MORSE_TABELLE, type ZeitPlan } from "../../shared/morse.js";
import { vorfuehrung, type HoerListe } from "./morse-hoerspur-liste.js";
import type { MorseHoerspur, SpurEreignis } from "./morse-hoerspur.js";
import { leuchte, musterBild, wenigBewegung } from "./morse-bild.js";
import { HOERSPUR } from "./morse-texte.js";
import { baueMeldung, dauerText, el, knopf, meldeIn, type MeldungsArt, type MorseStandDaten } from "./morse-teile.js";

interface PlatzOptionen {
  /** Wo er sitzt („hoeren“, „einfuehrung“, „tabelle“) — nur als Kennzeichen im DOM und in Tests. */
  ort: string;
  /** Beschriftung des Knopfs. */
  start: string;
  /** Die Zeile unter dem Knopf (aus der Liste, wenn es sie gibt). */
  hinweis: (liste: HoerListe) => string;
  /** Die Liste zum Zeitpunkt des Klicks — `null`: noch nichts zu spielen (der Platz bleibt verborgen). */
  liste: () => HoerListe | null;
  /** Groß und prominent (Vorführung) oder klein (Tabelle). */
  stil: "gross" | "klein";
  /** „Voriges/Nächstes Kapitel“ zeigen? Nur, wo es Kapitel gibt. */
  kapitelKnoepfe: boolean;
  /** Der Knopf wurde gedrückt: die Seite entscheidet (eine laufende Übung pausiert, dann startet der Abspieler). */
  beiStart: (platz: HoerspurPlatz) => void;
}

/** Die Bilder eines Schritts mit dem Ton leuchten lassen: je Zeichen sein Teil des Plans. Gibt den Halt zurück. */
export function leuchteBilder(bilder: readonly HTMLElement[], plan: ZeitPlan, inMs: number): () => void {
  const halte = bilder.map((b, j) => leuchte(b, { ...plan, toene: plan.toene.filter((t) => t.zeichen === j) }, inMs));
  return () => {
    for (const h of halte) h();
  };
}

/** Wie viele Minuten (ganz, mindestens eine) eine Liste dauert — für die Zeile unter dem Knopf. */
export function minuten(ms: number): number {
  return Math.max(1, Math.round(ms / 60_000));
}

let platzNr = 0;

/** Bei diesen Enden steht der Satz als Hinweis-Kasten, nicht als Fehler (morse-hoerspur.ts). */
const HINWEIS_GRUENDE: ReadonlySet<string> = new Set([
  "sprechtaste",
  "tagesaufnahme-nimmt-auf",
  "tagesaufnahme-frist",
  "tagesaufnahme-fehler",
  "zeitgrenze",
  "aufnahme",
]);

export class HoerspurPlatz {
  readonly wurzel: HTMLElement;
  private readonly startZeile: HTMLElement;
  private readonly startKnopf: HTMLButtonElement;
  private readonly hinweisZeile: HTMLElement;
  private readonly zurueckKnopf: HTMLButtonElement;
  private readonly meldung: HTMLElement;
  private readonly karte: HTMLElement;
  private readonly zustandZeile: HTMLElement;
  private readonly zustandWort: HTMLElement;
  private readonly kapitelZeile: HTMLElement;
  private readonly untertitel: HTMLElement;
  private readonly bildReihe: HTMLElement;
  private readonly balken: HTMLProgressElement;
  private readonly zeit: HTMLElement;
  private readonly haupt: HTMLButtonElement;
  private readonly hauptText: HTMLElement;
  private readonly stoppKnopf: HTMLButtonElement;
  private readonly voriges: HTMLButtonElement;
  private readonly naechstes: HTMLButtonElement;
  private readonly aufnahmeZeile: HTMLElement;
  private readonly aufnahmeText: HTMLElement;
  private bilder: HTMLElement[] = [];
  private leuchtHalt: () => void = () => undefined;
  private aufgebaut = "";
  private abmelden: () => void;

  constructor(
    private readonly engine: MorseHoerspur,
    private readonly o: PlatzOptionen,
  ) {
    const nr = ++platzNr;
    // Eine Gruppe, kein Merkpunkt (region): dieselbe Beschriftung steht an zwei Orten, und die Seite hat Merkpunkte genug.
    this.wurzel = el("div", `morse-spur morse-spur--${o.stil}`);
    this.wurzel.dataset.ort = o.ort;
    this.wurzel.dataset.zustand = "bereit";
    this.wurzel.setAttribute("role", "group");
    this.wurzel.setAttribute("aria-label", o.start);

    // Ruhig: der Knopf, die Zeile darunter, „Zurück zur Übung“
    this.startZeile = el("div", "morse-spur-start-zeile");
    this.startKnopf = knopf("", "morse-spur-start", () => undefined);
    const form = el("span", "morse-spur-start-form");
    form.setAttribute("aria-hidden", "true");
    this.startKnopf.append(form, el("span", "morse-spur-start-text", o.start));
    // Ein Doppeltipp startet EINE Wiedergabe: der zweite Klick (`detail` 2) zählt nicht.
    this.startKnopf.addEventListener("click", (ev) => {
      if (ev.detail > 1) return;
      this.o.beiStart(this);
    });
    this.zurueckKnopf = knopf(HOERSPUR.zurueckZurUebung, "morse-knopf morse-knopf--klein", () => undefined);
    // Wie „Zurück zu …“ in der Tabelle: dieselbe Klasse (der Pfeil davor), dazu die eigene für Tests und Maße.
    this.zurueckKnopf.classList.add("morse-zurueck", "morse-spur-zurueck");
    this.zurueckKnopf.hidden = true;
    this.startZeile.append(this.startKnopf, this.zurueckKnopf);
    this.hinweisZeile = el("p", "morse-hinweis morse-spur-hinweis");
    // Der kleine Platz (Tabelle) steht in EINEM Bedienstreifen: die Erklärung des Knopfs ist dort kein Absatz, sondern
    // Tooltip (`title`) und Beschreibung des Knopfs für Vorleser (`aria-describedby` darf auf ein verborgenes Element zeigen).
    if (o.stil === "klein") {
      this.hinweisZeile.id = `morse-spur-hinweis-${nr}`;
      this.hinweisZeile.hidden = true;
      this.startKnopf.setAttribute("aria-describedby", this.hinweisZeile.id);
    }
    this.meldung = baueMeldung();

    // Spielt: die Karte
    this.karte = el("div", "morse-spur-karte");
    this.karte.hidden = true;
    const kopf = el("div", "morse-spur-kopf");
    this.zustandZeile = el("p", "morse-spur-zustand");
    const zustandForm = el("span", "morse-spur-zustand-form");
    zustandForm.setAttribute("aria-hidden", "true");
    this.zustandWort = el("span", "morse-spur-zustand-wort");
    this.zustandZeile.append(zustandForm, this.zustandWort);
    this.zeit = el("p", "morse-zaehler morse-spur-zeit");
    kopf.append(this.zustandZeile, this.zeit);
    // Die EINE Live-Region: das Kapitel. Der Untertitel wechselt alle paar Sekunden und bleibt stumm.
    this.kapitelZeile = el("p", "morse-spur-kapitel");
    this.kapitelZeile.id = `morse-spur-kapitel-${nr}`;
    this.kapitelZeile.setAttribute("aria-live", "polite");
    this.untertitel = el("p", "morse-spur-untertitel");
    this.bildReihe = el("div", "morse-spur-bilder");
    this.bildReihe.setAttribute("aria-hidden", "true");
    this.balken = el("progress", "morse-spur-balken");
    this.balken.max = 1000;
    this.balken.value = 0;
    this.balken.setAttribute("aria-label", HOERSPUR.karte.fortschritt);
    const steuer = el("div", "morse-spur-steuer");
    this.haupt = el("button", "morse-spur-haupt");
    this.haupt.type = "button";
    const hauptForm = el("span", "morse-spur-haupt-form");
    hauptForm.setAttribute("aria-hidden", "true");
    this.hauptText = el("span", "morse-spur-haupt-text");
    this.haupt.append(hauptForm, this.hauptText);
    this.haupt.addEventListener("click", (ev) => {
      if (ev.detail > 1) return;
      this.engine.umschalten();
    });
    this.stoppKnopf = knopf(HOERSPUR.karte.stopp, "morse-spur-stopp", () => this.engine.stopp("knopf"));
    steuer.append(this.haupt, this.stoppKnopf);
    const kapitelSteuer = el("div", "morse-spur-kapitel-steuer");
    this.voriges = knopf(HOERSPUR.karte.voriges, "morse-knopf morse-knopf--klein", () => this.engine.springe(-1));
    this.voriges.setAttribute("aria-label", HOERSPUR.karte.vorigesLang);
    this.naechstes = knopf(HOERSPUR.karte.naechstes, "morse-knopf morse-knopf--klein", () => this.engine.springe(1));
    this.naechstes.setAttribute("aria-label", HOERSPUR.karte.naechstesLang);
    kapitelSteuer.append(this.voriges, this.naechstes);
    kapitelSteuer.hidden = !o.kapitelKnoepfe;
    // Die angehaltene Tagesaufnahme: Form wie die Pause-Pille oben (zwei Balken), der Satz in normaler Schrift.
    this.aufnahmeZeile = el("p", "morse-spur-aufnahme");
    this.aufnahmeZeile.setAttribute("role", "status");
    this.aufnahmeZeile.hidden = true;
    const aufnahmeForm = el("span", "morse-spur-aufnahme-form");
    aufnahmeForm.setAttribute("aria-hidden", "true");
    this.aufnahmeText = el("span", "morse-spur-aufnahme-text");
    this.aufnahmeZeile.append(aufnahmeForm, this.aufnahmeText);
    // Die Knöpfe oben, der wechselnde Text darunter: ein langer Untertitel schiebt nichts unter dem Finger weg.
    this.karte.append(kopf, this.balken, steuer, kapitelSteuer, this.aufnahmeZeile, this.kapitelZeile, this.untertitel, this.bildReihe);

    this.wurzel.append(this.startZeile, this.hinweisZeile, this.meldung, this.karte);
    this.abmelden = engine.hoere((e) => this.beiEreignis(e));
    this.aktualisiere();
  }

  /** Dieser Platz hat den Abspieler gestartet und er ist aktiv? */
  private get meine(): boolean {
    return this.engine.aktiv && this.engine.besitzer === this;
  }

  get aktiv(): boolean {
    return this.meine;
  }

  liste(): HoerListe | null {
    return this.o.liste();
  }

  /** Die Zeile unter dem Knopf und ob der Platz überhaupt da ist (ohne Liste: verborgen). */
  aktualisiere(): void {
    const l = this.o.liste();
    this.wurzel.hidden = l === null;
    if (l) {
      this.hinweisZeile.textContent = this.o.hinweis(l);
      if (this.o.stil === "klein") this.startKnopf.title = this.hinweisZeile.textContent;
    }
  }

  /** „Zurück zur Übung“ zeigen (Klick-Behandlung setzt der Dienst) oder verbergen. */
  setzeZurueck(an: boolean, beiKlick?: () => void): void {
    this.zurueckKnopf.hidden = !an;
    this.zurueckKnopf.onclick = beiKlick ? () => beiKlick() : null;
  }

  /** Den Fokus auf den Pause-Knopf (nach dem Start, aus der Geste): so bedient die Leertaste ihn. */
  fokusAufHaupt(): void {
    this.haupt.focus({ preventScroll: true });
  }

  zerstoere(): void {
    this.abmelden();
    this.leuchtHalt();
  }

  /* ── Was der Abspieler sagt ─────────────────────────────────────── */

  private beiEreignis(e: SpurEreignis): void {
    if (e.art === "zustand") return this.zeichneZustand();
    if (e.art === "ende") {
      if (e.besitzer !== this) return;
      this.leuchtHalt();
      this.leuchtHalt = () => undefined;
      const text = e.meldung || (e.fertig ? (e.liste?.id === "tabelle" ? HOERSPUR.meldung.fertigTabelle : HOERSPUR.meldung.fertig) : "");
      // Eine Aufnahme geht vor (Sprechtaste, Tagesaufnahme, Notbremse): kein Fehler, aber deutlich — als Kasten.
      const art: MeldungsArt = e.meldung ? (HINWEIS_GRUENDE.has(e.grund) ? "hinweis" : "fehler") : "gut";
      meldeIn(this.meldung, text, art);
      return;
    }
    if (!this.meine) return;
    if (e.art === "schritt") return this.zeichneSchritt();
    if (e.art === "ton") {
      this.leuchtHalt();
      this.leuchtHalt = e.schritt.zeigen.length && this.bilder.length === e.schritt.zeigen.length ? leuchteBilder(this.bilder, e.plan, e.inMs) : () => undefined;
    }
  }

  private zeichneZustand(): void {
    const aktiv = this.engine.aktiv;
    const meine = this.meine;
    // Der Fokus war auf der Karte, die jetzt verschwindet: zurück auf den Knopf (sonst bliebe er im Nichts, und
    // die Leertaste gehörte der Seite).
    const hatteFokus = !meine && this.karte.contains(document.activeElement);
    this.wurzel.dataset.zustand = meine ? this.engine.zustand : "bereit";
    // Ist anderswo etwas aktiv, gibt es hier keinen zweiten Knopf; bei mir die Karte statt des Knopfs.
    this.startZeile.hidden = aktiv;
    this.hinweisZeile.hidden = aktiv || this.o.stil === "klein";
    this.karte.hidden = !meine;
    if (aktiv) meldeIn(this.meldung, "");
    if (hatteFokus) this.startKnopf.focus({ preventScroll: true });
    if (!meine) return;
    const spielt = this.engine.spielt;
    // In der Pause leuchtet nichts mehr nach (der Ton ist aus, das Bild darf nicht weiterlaufen).
    if (!spielt) {
      this.leuchtHalt();
      this.leuchtHalt = () => undefined;
    }
    this.zeichneAufnahme();
    this.zustandZeile.dataset.zustand = this.engine.zustand;
    this.zustandWort.textContent = spielt ? HOERSPUR.karte.zustand.spielt : HOERSPUR.karte.zustand.pause;
    this.haupt.dataset.rolle = spielt ? "pause" : "weiter";
    this.hauptText.textContent = spielt ? HOERSPUR.karte.pause : HOERSPUR.karte.weiter;
    this.zeichneSchritt();
  }

  /** Der Satz zur angehaltenen Tagesaufnahme — nur, solange sie für diese Karte angehalten ist. */
  private zeichneAufnahme(): void {
    const l = this.engine.liste;
    const an = this.meine && this.engine.tagesaufnahmeAngehalten && l !== null;
    this.aufnahmeZeile.hidden = !an;
    const text = an ? HOERSPUR.meldung.tagesaufnahmeAngehalten(l.id) : "";
    if (this.aufnahmeText.textContent !== text) this.aufnahmeText.textContent = text;
  }

  private zeichneSchritt(): void {
    const l = this.engine.liste;
    const s = this.engine.aktuellerSchritt;
    if (!l || !s) return;
    const k = this.engine.kapitel;
    const kapitel = l.kapitel[k];
    // Mehrere Kapitel: „Kapitel 2 von 4: …“; eine Liste mit einem Kapitel (die Tabelle) nennt sich selbst.
    const titel = l.kapitel.length > 1 ? HOERSPUR.karte.kapitel(k + 1, l.kapitel.length, kapitel.titel) : l.titel;
    if (this.kapitelZeile.textContent !== titel) this.kapitelZeile.textContent = titel;
    if (this.untertitel.textContent !== s.untertitel) this.untertitel.textContent = s.untertitel;
    // Die Bilder nur neu bauen, wenn sich etwas ändert (sonst bräche das Leuchten ab).
    const schluessel = `${s.zeigen.join("|")}#${s.namen}`;
    if (schluessel !== this.aufgebaut) {
      this.aufgebaut = schluessel;
      this.leuchtHalt();
      this.leuchtHalt = () => undefined;
      this.baueBilder(s.zeigen, s.namen);
    }
    const f = this.engine.fortschritt();
    this.balken.value = f.gesamt > 0 ? Math.round((f.ms / f.gesamt) * 1000) : 0;
    this.zeit.textContent =
      l.zaehlung === "zeichen"
        ? HOERSPUR.tabelle.zeichen(this.engine.schritt + 1, l.schritte)
        : HOERSPUR.karte.zeit(dauerText(f.ms), dauerText(f.gesamt));
    const fokus = document.activeElement;
    this.voriges.disabled = k === 0 && this.engine.schritt === 0;
    this.naechstes.disabled = k >= l.kapitel.length - 1;
    // Ein Knopf, der den Fokus hatte und jetzt aus ist, gibt ihn dem Pause-Knopf (nie ins Leere).
    if ((fokus === this.voriges && this.voriges.disabled) || (fokus === this.naechstes && this.naechstes.disabled)) this.fokusAufHaupt();
  }

  private baueBilder(zeigen: readonly string[], namen: boolean): void {
    this.bildReihe.textContent = "";
    this.bilder = [];
    this.bildReihe.hidden = zeigen.length === 0;
    // Wenige Töne: große Bilder; viele (drei Zeichen, eine Gruppe): mittlere, damit die Reihe nicht umbricht.
    const elemente = zeigen.reduce((n, z) => n + (MORSE_TABELLE[z] ?? "").length, 0);
    for (const z of zeigen) {
      const t = el("span", "morse-spur-zeichen");
      const bild = musterBild(MORSE_TABELLE[z] ?? "", elemente <= 6 ? "l" : "m");
      if (namen) t.append(el("span", "morse-spur-name", z));
      t.append(bild);
      this.bildReihe.appendChild(t);
      this.bilder.push(bild);
    }
  }

  /**
   * Die Karte ins Bild holen: die der Vorführung in die Mitte (sie ist hoch — am Telefon läge ihr Untertitel sonst
   * unter dem Rand; „ganz oben“ läge sie unter dem Seitenkopf, dessen Höhe sich mit der Breite ändert). Die der
   * Tabelle nicht: sie klebt oben, und ins Bild geholt wird die Karte des Zeichens,
   * das dran ist (morse-tabelle.ts) — ein zweites Scrollen hier risse sie wieder weg (gemessen: die erste Karte blieb
   * unten aus dem Fenster).
   */
  insBild(): void {
    if (this.o.stil === "klein") return;
    try {
      this.karte.scrollIntoView({ block: "center", behavior: wenigBewegung() ? "auto" : "smooth" });
    } catch {
      /* ältere Browser */
    }
  }
}

/* ── Der Dienst für die Seite ──────────────────────────────────────── */

interface HoerspurDienstAbhaengigkeiten {
  /**
   * Eine laufende Übung pausieren, weil die Hörspur Ton braucht (wie bei der Tabelle). Gibt zurück, wie es dort
   * weitergeht („Zurück zur Übung“), oder `null`, wenn keine Übung lief.
   */
  pausiereUebung: () => { zurueck: () => void; wartet: () => boolean } | null;
}

export class HoerspurDienst {
  private readonly plaetze = new Set<HoerspurPlatz>();
  /** Eine Übung, die der Hörspur weichen musste — bis sie fortgesetzt oder verlassen ist. */
  private herkunft: { zurueck: () => void; wartet: () => boolean } | null = null;
  private readonly abmelden: () => void;

  constructor(
    readonly engine: MorseHoerspur,
    private readonly d: HoerspurDienstAbhaengigkeiten,
  ) {
    this.abmelden = engine.hoere((e) => {
      if (e.art === "zustand") this.zeichneZurueck();
    });
  }

  /** Einen Platz bauen und merken. */
  platz(o: Omit<PlatzOptionen, "beiStart">): HoerspurPlatz {
    const p = new HoerspurPlatz(this.engine, { ...o, beiStart: (platz) => this.starte(platz) });
    this.plaetze.add(p);
    this.zeichneZurueck();
    return p;
  }

  /** Der Platz der Vorführung (Hören/Bereit, Einführung): die Liste folgt dem Stand des Kontos — Stufe und Tempo. */
  vorfuehrungPlatz(ort: string, stand: () => Pick<MorseStandDaten, "stufe" | "einstellungen"> | null): HoerspurPlatz {
    return this.platz({
      ort,
      start: HOERSPUR.vorfuehrung.start,
      hinweis: (l) => HOERSPUR.vorfuehrung.hinweis(minuten(l.ms)),
      liste: () => {
        const s = stand();
        return s ? vorfuehrung(s.stufe, s.einstellungen) : null;
      },
      stil: "gross",
      kapitelKnoepfe: true,
    });
  }

  /** Alle Plätze neu lesen (Stand und Einstellungen haben sich geändert). */
  aktualisiere(): void {
    for (const p of this.plaetze) p.aktualisiere();
  }

  /** Der Knopf eines Platzes: erst die Übung pausieren (falls eine läuft), dann der Abspieler — aus der Geste. */
  private starte(platz: HoerspurPlatz): void {
    const liste = platz.liste();
    if (!liste) return;
    const h = this.d.pausiereUebung();
    if (h) this.herkunft = h;
    if (this.engine.starte(liste, { besitzer: platz })) {
      platz.fokusAufHaupt();
      platz.insBild();
    }
    this.zeichneZurueck();
  }

  /** Der Hörspur sagen, dass etwas anderes Ton braucht — sie hält an (nichts, wenn sie nicht spielt). */
  stille(grund: string): void {
    if (this.engine.aktiv) this.engine.stopp(grund);
  }

  /** Nur anhalten, wenn DIESER Platz es ist, der spielt (die Einführung wird zugeklappt). */
  stilleFuer(platz: HoerspurPlatz, grund: string): void {
    if (this.engine.aktiv && this.engine.besitzer === platz) this.engine.stopp(grund);
  }

  /** „Zurück zur Übung“: in allen Plätzen, solange die Hörspur ruht und die Übung noch wartet. */
  private zeichneZurueck(): void {
    if (this.herkunft && !this.engine.aktiv && !this.herkunft.wartet()) this.herkunft = null;
    const an = this.herkunft !== null && !this.engine.aktiv;
    for (const p of this.plaetze) {
      p.setzeZurueck(
        an,
        an
          ? () => {
              const h = this.herkunft;
              this.herkunft = null;
              this.zeichneZurueck();
              h?.zurueck();
            }
          : undefined,
      );
    }
  }

  zerstoere(): void {
    this.abmelden();
    this.engine.stopp("zerstoert");
    for (const p of this.plaetze) p.zerstoere();
    this.plaetze.clear();
  }
}
